import { Box, InputLabel, TextField } from '@mui/material';
import dayjs from 'dayjs';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, ClockIcon } from '~/assets/icons';
import { BaseDatePicker, BaseRow, BaseTimePicker } from '~/components/common';
import { IJob, IUpdateJobPayload } from '~/utils/interface/job';
import styles from './Date.module.scss';
import classNames from 'classnames/bind';
import { useFormik } from 'formik';
import { handleUpdateJob } from '~/thunks/job/jobThunk';
import { cast, handleApiError, notify } from '~/utils/helper';
import { useAppDispatch } from '~/redux/hooks';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';

type DateProps = {
  data: IJob;
  updateData: () => void;
};

interface IDatePayload {
  jobCreatedAt?: string;
  firstContactedAt?: string;
}

const cx = classNames.bind(styles);
const initialValues: IUpdateJobPayload = {
  job: {
    jobCreatedAt: '',
    firstContactedAt: '',
    dateAssessmentScheduled: '',
    dateInspected: '',
    dateEstimateSent: '',
    dateReadyForProduction: '',
    dateInProduction: '',
    dateWorkCompleted: '',
    dateInvoiced: '',
    dateClosed: '',
    dateLost: '',

    dateEstimatedStart: '',
    dateEstimatedCompletion: '',
    dateDepositDeductibleCollected: '',
    dateWarrantyIssue: '',
  },
};

const Date = (props: DateProps) => {
  //#region Destructuring Props
  const { data } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job', 'admin/common']);
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      const submitValue = { ...values };

      for (let i in submitValue.job) {
        const index = i as keyof IDatePayload;
        if (!submitValue.job[index]) {
          delete submitValue.job[index];
          continue;
        }
        submitValue.job[index] = dayjs(submitValue.job[index]).format('YYYY-MM-DD HH:mm:ss');
      }

      try {
        await dispatch(handleUpdateJob({ id: Number(data.id), params: submitValue })).unwrap();
        notify('success', t('editSuccess', { ns: 'admin/common' }));
        if (props.updateData) props.updateData();
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });
  const [lastActivityAtText, setLastActivityAtText] = useState<string>('');
  const [jobCreatedAtText, setJobCreatedAtText] = useState<string>('');
  const [firstContactedAtText, setFirstContactedAtText] = useState<string>('');
  // Additional date fields
  const [dateAssessmentScheduledText, setDateAssessmentScheduledText] = useState<string>('');
  const [dateInspectedText, setDateInspectedText] = useState<string>('');
  const [dateEstimateSentText, setDateEstimateSentText] = useState<string>('');
  const [dateReadyForProductionText, setDateReadyForProductionText] = useState<string>('');
  const [dateInProductionText, setDateInProductionText] = useState<string>('');
  const [dateWorkCompletedText, setDateWorkCompletedText] = useState<string>('');
  const [dateInvoicedText, setDateInvoicedText] = useState<string>('');
  const [dateClosedText, setDateClosedText] = useState<string>('');
  const [dateLostText, setDateLostText] = useState<string>('');

  const [dateEstimatedStartText, setDateEstimatedStartText] = useState<string>('');
  const [dateEstimatedCompletionText, setDateEstimatedCompletionText] = useState<string>('');
  const [dateDepositDeductibleCollectedText, setDateDepositDeductibleCollectedText] = useState<string>('');
  const [dateWarrantyIssueText, setDateWarrantyIssueText] = useState<string>('');

  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    form.setFieldValue('job.jobCreatedAt', cast(data.jobCreatedAt));
    form.setFieldValue('job.firstContactedAt', cast(data.firstContactedAt));
    form.setFieldValue('job.dateAssessmentScheduled', cast(data.dateAssessmentScheduled));
    form.setFieldValue('job.jobCreatedAt', cast(data.jobCreatedAt));
    form.setFieldValue('job.firstContactedAt', cast(data.firstContactedAt));
    form.setFieldValue('job.dateAssessmentScheduled', cast(data.dateAssessmentScheduled));
    form.setFieldValue('job.dateInspected', cast(data.dateInspected));
    form.setFieldValue('job.dateEstimateSent', cast(data.dateEstimateSent));
    form.setFieldValue('job.dateReadyForProduction', cast(data.dateReadyForProduction));
    form.setFieldValue('job.dateInProduction', cast(data.dateInProduction));
    form.setFieldValue('job.dateWorkCompleted', cast(data.dateWorkCompleted));
    form.setFieldValue('job.dateInvoiced', cast(data.dateInvoiced));
    form.setFieldValue('job.dateClosed', cast(data.dateClosed));
    form.setFieldValue('job.dateLost', cast(data.dateLost));

    form.setFieldValue('job.dateEstimatedStart', cast(data.dateEstimatedStart));
    form.setFieldValue('job.dateEstimatedCompletion', cast(data.dateEstimatedCompletion));
    form.setFieldValue('job.dateDepositDeductibleCollected', cast(data.dateDepositDeductibleCollected));
    form.setFieldValue('job.dateWarrantyIssue', cast(data.dateWarrantyIssue));

    setLastActivityAtText(data.lastActivityAt ? dayjs(data.lastActivityAt).format('MM/DD/YYYY hh:mm A') : '');
    setJobCreatedAtText(data.jobCreatedAt ? dayjs(data.jobCreatedAt).format('MM/DD/YYYY hh:mm A') : '');
    setFirstContactedAtText(data.firstContactedAt ? dayjs(data.firstContactedAt).format('MM/DD/YYYY hh:mm A') : '');
    // Additional date formatting and setting
    setDateAssessmentScheduledText(
      data.dateAssessmentScheduled ? dayjs(data.dateAssessmentScheduled).format('MM/DD/YYYY hh:mm A') : ''
    );
    setDateInspectedText(data.dateInspected ? dayjs(data.dateInspected).format('MM/DD/YYYY hh:mm A') : '');
    setDateEstimateSentText(data.dateEstimateSent ? dayjs(data.dateEstimateSent).format('MM/DD/YYYY hh:mm A') : '');
    setDateReadyForProductionText(
      data.dateReadyForProduction ? dayjs(data.dateReadyForProduction).format('MM/DD/YYYY hh:mm A') : ''
    );
    setDateInProductionText(data.dateInProduction ? dayjs(data.dateInProduction).format('MM/DD/YYYY hh:mm A') : '');
    setDateWorkCompletedText(data.dateWorkCompleted ? dayjs(data.dateWorkCompleted).format('MM/DD/YYYY hh:mm A') : '');
    setDateInvoicedText(data.dateInvoiced ? dayjs(data.dateInvoiced).format('MM/DD/YYYY hh:mm A') : '');
    setDateClosedText(data.dateClosed ? dayjs(data.dateClosed).format('MM/DD/YYYY hh:mm A') : '');
    setDateLostText(data.dateLost ? dayjs(data.dateLost).format('MM/DD/YYYY hh:mm A') : '');

    setDateEstimatedStartText(
      data.dateEstimatedStart ? dayjs(data.dateEstimatedStart).format('MM/DD/YYYY hh:mm A') : ''
    );
    setDateEstimatedCompletionText(
      data.dateEstimatedCompletion ? dayjs(data.dateEstimatedCompletion).format('MM/DD/YYYY hh:mm A') : ''
    );
    setDateDepositDeductibleCollectedText(
      data.dateDepositDeductibleCollected ? dayjs(data.dateDepositDeductibleCollected).format('MM/DD/YYYY hh:mm A') : ''
    );
    setDateWarrantyIssueText(data.dateWarrantyIssue ? dayjs(data.dateWarrantyIssue).format('MM/DD/YYYY hh:mm A') : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  //#endregion Implement Hook

  //#region Handle Function
  const renderEndAdornment = (key: string) => {
    let rawDate = form.values.job[key as keyof IDatePayload];
    const date = rawDate ? dayjs(rawDate).toDate() : undefined;

    return (
      <>
        <Box
          className={cx('flex w-[100px]', {
            'pointer-events-none	': !permissions[PERMISSIONS.project_edit],
          })}
        >
          <Box className='w-[46px]'>
            <BaseDatePicker
              value={date}
              customInput={
                <Box className='flex justify-center items-center w-[46px] h-[36px]'>
                  <CalendarIcon className='w-[16px] h-[16px]' />
                </Box>
              }
              onChange={(value) => {
                form.setFieldValue(`job.${key}`, value);
                form.submitForm();
              }}
            />
          </Box>
          <Box className='w-[46px]'>
            <BaseTimePicker
              value={date}
              customInput={
                <Box className='flex justify-center items-center w-[46px] h-[36px]'>
                  <ClockIcon className='w-[16px] h-[16px]' />
                </Box>
              }
              onChange={(value) => {
                form.setFieldValue(`job.${key}`, value);
                form.submitForm();
              }}
            />
          </Box>
        </Box>
      </>
    );
  };
  //#endregion Handle Function

  return (
    <>
      <BaseRow className={cx('date-container', 'gap-[30px]')}>
        <Box className='w-full'>
          <InputLabel>{t('date.lastActivityAt')}</InputLabel>
          <TextField value={lastActivityAtText} disabled />
        </Box>
        <Box className='w-full'>
          <Box>
            <InputLabel>{t('date.jobCreatedAt')}</InputLabel>
            <TextField
              value={jobCreatedAtText}
              InputProps={{ endAdornment: renderEndAdornment('jobCreatedAt') }}
              disabled
              error={Boolean(form.errors?.job?.jobCreatedAt)}
              helperText={form.errors?.job?.jobCreatedAt}
              className={cx('date-input')}
            />
          </Box>
        </Box>
        <Box className='w-full'>
          <Box>
            <InputLabel>{t('date.firstContactedAt')}</InputLabel>
            <TextField
              value={firstContactedAtText}
              InputProps={{ endAdornment: renderEndAdornment('firstContactedAt') }}
              disabled
              error={Boolean(form.errors?.job?.firstContactedAt)}
              helperText={form.errors?.job?.firstContactedAt}
              className={cx('date-input')}
            />
          </Box>
        </Box>
      </BaseRow>
      <BaseRow className={cx('date-container', 'gap-[30px]')}>
        {/* Row 2 */}
        <Box className='w-full'>
          <InputLabel>{t('date.dateAssessmentScheduled')}</InputLabel>
          <TextField
            value={dateAssessmentScheduledText}
            InputProps={{ endAdornment: renderEndAdornment('dateAssessmentScheduled') }}
            disabled
            error={Boolean(form.errors?.job?.dateAssessmentScheduled)}
            helperText={form.errors?.job?.dateAssessmentScheduled}
            className={cx('date-input')}
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('date.dateInspected')}</InputLabel>
          <TextField
            value={dateInspectedText}
            InputProps={{ endAdornment: renderEndAdornment('dateInspected') }}
            disabled
            error={Boolean(form.errors?.job?.dateInspected)}
            helperText={form.errors?.job?.dateInspected}
            className={cx('date-input')}
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('date.dateEstimateSent')}</InputLabel>
          <TextField
            value={dateEstimateSentText}
            InputProps={{ endAdornment: renderEndAdornment('dateEstimateSent') }}
            disabled
            error={Boolean(form.errors?.job?.dateEstimateSent)}
            helperText={form.errors?.job?.dateEstimateSent}
            className={cx('date-input')}
          />
        </Box>
      </BaseRow>
      <BaseRow className={cx('date-container', 'gap-[30px]')}>
        {/* Row 3 */}
        <Box className='w-full'>
          <InputLabel>{t('date.dateEstimatedStart')}</InputLabel>
          <TextField
            value={dateEstimatedStartText}
            InputProps={{ endAdornment: renderEndAdornment('dateEstimatedStart') }}
            disabled
            error={Boolean(form.errors?.job?.dateEstimatedStart)}
            helperText={form.errors?.job?.dateEstimatedStart}
            className={cx('date-input')}
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('date.dateEstimatedCompletion')}</InputLabel>
          <TextField
            value={dateEstimatedCompletionText}
            InputProps={{ endAdornment: renderEndAdornment('dateEstimatedCompletion') }}
            disabled
            error={Boolean(form.errors?.job?.dateEstimatedCompletion)}
            helperText={form.errors?.job?.dateEstimatedCompletion}
            className={cx('date-input')}
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('date.dateReadyForProduction')}</InputLabel>
          <TextField
            value={dateReadyForProductionText}
            InputProps={{ endAdornment: renderEndAdornment('dateReadyForProduction') }}
            disabled
            error={Boolean(form.errors?.job?.dateReadyForProduction)}
            helperText={form.errors?.job?.dateReadyForProduction}
            className={cx('date-input')}
          />
        </Box>
      </BaseRow>
      <BaseRow className={cx('date-container', 'gap-[30px]')}>
        {/* Row 4 */}
        <Box className='w-full'>
          <InputLabel>{t('date.dateDepositDeductibleCollected')}</InputLabel>
          <TextField
            value={dateDepositDeductibleCollectedText}
            InputProps={{ endAdornment: renderEndAdornment('dateDepositDeductibleCollected') }}
            disabled
            error={Boolean(form.errors?.job?.dateDepositDeductibleCollected)}
            helperText={form.errors?.job?.dateDepositDeductibleCollected}
            className={cx('date-input')}
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('date.dateInProduction')}</InputLabel>
          <TextField
            value={dateInProductionText}
            InputProps={{ endAdornment: renderEndAdornment('dateInProduction') }}
            disabled
            error={Boolean(form.errors?.job?.dateInProduction)}
            helperText={form.errors?.job?.dateInProduction}
            className={cx('date-input')}
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('date.dateWorkCompleted')}</InputLabel>
          <TextField
            value={dateWorkCompletedText}
            InputProps={{ endAdornment: renderEndAdornment('dateWorkCompleted') }}
            disabled
            error={Boolean(form.errors?.job?.dateWorkCompleted)}
            helperText={form.errors?.job?.dateWorkCompleted}
            className={cx('date-input')}
          />
        </Box>
      </BaseRow>

      <BaseRow className={cx('date-container', 'gap-[30px]')}>
        <Box className='w-full'>
          <InputLabel>{t('date.dateInvoiced')}</InputLabel>
          <TextField
            value={dateInvoicedText}
            InputProps={{ endAdornment: renderEndAdornment('dateInvoiced') }}
            disabled
            error={Boolean(form.errors?.job?.dateInvoiced)}
            helperText={form.errors?.job?.dateInvoiced}
            className={cx('date-input')}
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('date.dateClosed')}</InputLabel>
          <TextField
            value={dateClosedText}
            InputProps={{ endAdornment: renderEndAdornment('dateClosed') }}
            disabled
            error={Boolean(form.errors?.job?.dateClosed)}
            helperText={form.errors?.job?.dateClosed}
            className={cx('date-input')}
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('date.dateLost')}</InputLabel>
          <TextField
            value={dateLostText}
            InputProps={{ endAdornment: renderEndAdornment('dateLost') }}
            disabled
            error={Boolean(form.errors?.job?.dateLost)}
            helperText={form.errors?.job?.dateLost}
            className={cx('date-input')}
          />
        </Box>
      </BaseRow>

      <BaseRow className={cx('date-container', 'gap-[30px]')}>
        {/* Row 6 */}

        <Box className='w-full'>
          <InputLabel>{t('date.dateWarrantyIssue')}</InputLabel>
          <TextField
            value={dateWarrantyIssueText}
            InputProps={{ endAdornment: renderEndAdornment('dateWarrantyIssue') }}
            disabled
            error={Boolean(form.errors?.job?.dateWarrantyIssue)}
            helperText={form.errors?.job?.dateWarrantyIssue}
            className={cx('date-input')}
          />
        </Box>
        <Box className='w-full'></Box>
        <Box className='w-full'></Box>
      </BaseRow>
    </>
  );
};

export default memo(Date);
